import React, { InputHTMLAttributes, useEffect } from 'react';

import {
  FieldError,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
  UseFormUnregister,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { Label } from '../Label/Label';

import { FormInputWarningMessage } from './FormInputWarningMessage';

interface IProps<FormState extends FieldValues = object> {
  error?: FieldError;
  label: string;
  id: Path<FormState>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  register: UseFormRegister<FormState>;
  unregister?: UseFormUnregister<FormState>;
  registerOptions?: RegisterOptions;
  className?: string;
  customLabelStyles?: string;
  customInputStyles?: string;
}
export function HooksFormInput<FormState extends FieldValues>({
  label,
  error,
  id,
  inputProps = {},
  register,
  registerOptions = {},
  className = '',
  customLabelStyles = '',
  customInputStyles = '',
  unregister,
  ...rest
}: IProps<FormState>) {
  useEffect(() => {
    return () => {
      if (unregister) {
        unregister(id);
      }
    };
  }, [id, unregister]);
  return (
    <div className={`${className} flex flex-col`}>
      <Label
        htmlFor={id}
        customStyles={customLabelStyles}
        required={registerOptions.required}
        disabled={inputProps.disabled}
        data-testid={`${id}-label`}
      >
        {label}
      </Label>
      <input
        id={id}
        placeholder={label}
        className={twMerge(
          `placeholder:text-midnight focus:border-abyss disabled:border-disabled disabled:bg-lake-fog disabled:text-disabled disabled:placeholder:text-disabled rounded-lg border p-3 text-base`,
          error ? 'border-error-shade' : 'border-twilight',
          customInputStyles,
        )}
        {...inputProps}
        {...register(id, registerOptions)}
        {...rest}
      />
      {error?.message && (
        <FormInputWarningMessage dataTestid={`${id}-error`} variant="error" text={error.message} />
      )}
    </div>
  );
}
